import { ITEMS_PER_PAGE } from "#constants";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  usePagination,
  useSort,
  type GroupingType,
  type PaginationType,
  type SortingType,
} from "@validereinc/common-components";
import { SortDirection } from "@validereinc/domain";
import { usePrevious } from "@validereinc/domain-controllers";
import { isEmpty, isEqual } from "es-toolkit/compat";
import { produce } from "immer";
import { useCallback, useEffect, useRef, useState } from "react";

export type TableStateType = PaginationType & SortingType & GroupingType;

/**
 * Determines if the page needs to be reset back to 1 and data be fetched for
 * that page instead. Keeps track of any applied filters and the page # + total
 * results during the time of filter change - then sets a flag to indicate that
 * resetting should occur. when this hook runs again, the filters will not have
 * changed so what we're waiting for instead is for the page # or total # of
 * results to be different - if either of those changed, that means no data has
 * come in and we're safe to turn off the resetting flag.
 *
 * The reason we'd want to do this all is because if you're on any page but 1,
 * and apply filters that would return less # of results than the page you're
 * on, with the way the BE APIs work (CarbonHub in particular) you make a
 * request for that page and get no results back - which is incorrect because
 * the total entries usually indicates that there is data... in the smaller
 * pages. The UI also doesn't help in this case because the pagination controls
 * dissapear... so it looks like there indeed is no data for the filters you
 * applied. For this reason, whenever filters change, this hook helps you just
 * reset the page back to 1.
 * @param pageAndTotalRecords the current page and total # of results for that page for any data fetched
 * @param newFilters any filters applied to get the data
 * @returns should the page be reset back to 1 and fetch data for that page instead?
 */
export const useShouldPageReset = (
  pageAndTotalRecords: Pick<PaginationType, "page" | "total">,
  newFilters: object = {}
) => {
  const { page: newPage, total: newTotal } = pageAndTotalRecords;
  const filters = useRef(newFilters);
  const pageDuringFilterChange = useRef<number | null>(newPage ?? null);
  const totalDuringFilterChange = useRef<number | null>(newTotal ?? null);
  const shouldReset = useRef(false);

  // if filters changed, sync the ref, flag for resetting page, and remember the page and # of results at this time
  if (!isEqual(filters.current, newFilters)) {
    filters.current = newFilters;
    pageDuringFilterChange.current = newPage;
    totalDuringFilterChange.current = newTotal ?? 0;
    shouldReset.current = true;
  } else if (
    // if a re-render happens for any reason and the page # or the total # of results has changed
    // that means new data has or is coming in, so we can turn off the resetting flag
    pageDuringFilterChange.current !== newPage ||
    totalDuringFilterChange.current !== newTotal
  ) {
    shouldReset.current = false;
    pageDuringFilterChange.current = null;
    totalDuringFilterChange.current = null;
  }

  return shouldReset.current;
};

/**
 * Keep track of sorting and pagination props, resetting the page back to 1 if filters change
 * @param initialState any combination of sorting / pagination props to use by default during first load
 * @param filters filters applied to fetching data
 * @returns [tableState, updateTableState] - pass tableState to the pagination props in the DataTable. pass updateTableState to the onPaginationChange and onSortChange props of the DataTable
 */
export const useTableSortingAndPagination = (
  initialState: Partial<TableStateType> = {},
  filters?: object,
  storageKey?: string
) => {
  const [tableState, setTableState] = useStickyState<TableStateType>(
    {
      page: 1,
      pageSize: ITEMS_PER_PAGE,
      total: 0,
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
      groupBy: [],
      ...initialState,
    },
    storageKey ?? "table-state"
  );

  const updateTableState = (update: Partial<TableStateType>) => {
    setTableState((prev) => ({
      ...prev,
      ...update,
    }));
  };

  const shouldResetPage = useShouldPageReset(tableState, filters);

  useEffect(() => {
    if (shouldResetPage) {
      updateTableState({ page: 1 });
    }
  }, [shouldResetPage]);

  return [tableState, updateTableState] as const;
};

export const useTablesSortingAndPagination = (
  tableKeys: string[],
  sharedInitialState: Partial<TableStateType> = {},
  storageKey?: string
) => {
  const [tableStates, setTableStates] = useStickyState<
    Map<string, TableStateType>
  >(
    new Map(
      tableKeys
        .filter((k) => k != undefined && k != null)
        .map((k) => [
          k,
          {
            page: 1,
            pageSize: ITEMS_PER_PAGE,
            sortBy: "created_at",
            sortDirection: SortDirection.DESCENDING,
            groupBy: [],
            ...sharedInitialState,
          },
        ])
    ),
    storageKey ?? "group-by-tables-state"
  );

  const updateTableStates = (
    state: Map<string, TableStateType>,
    action:
      | {
          type: "create" | "update";
          key: string;
          tableState: Partial<TableStateType>;
        }
      | {
          type: "delete";
          key: string;
        }
      | {
          type: "clear";
        }
  ) => {
    if (action.type === "create") {
      setTableStates((prev) =>
        produce(prev, (draft) =>
          draft.set(action.key, {
            page: 1,
            pageSize: 25,
            groupBy: [],
            ...sharedInitialState,
            sortBy: "created_at",
            sortDirection: SortDirection.DESCENDING,
            ...action.tableState,
          })
        )
      );
    }
    if (action.type === "update") {
      if (action.key && state.has(action.key)) {
        setTableStates((prev) =>
          produce(prev, (draft) =>
            draft.set(action.key, {
              page: 1,
              pageSize: 25,
              sortBy: "created_at",
              sortDirection: SortDirection.DESCENDING,
              groupBy: [],
              ...(state.get(action.key) ?? {}),
              ...action.tableState,
            })
          )
        );
      }
    } else if (action.type === "delete") {
      if (action.key && state.has(action.key)) {
        setTableStates((prev) =>
          produce(prev, (draft) => {
            draft.delete(action.key);
          })
        );
      }
    } else if (action.type === "clear") {
      setTableStates((prev) =>
        produce(prev, (draft) => {
          draft.clear();
        })
      );
    }
  };
  const previousTableKeys = usePrevious(tableKeys);

  // keep tableKeys in sync with table states at all times
  useEffect(() => {
    // Do not reset state if we already have saved the state and the previousTableKeys are empty
    // e.g. when refreshing the page or when closing and reopening the page
    if (!isEmpty(tableStates) && isEmpty(previousTableKeys)) return;

    if (!tableKeys || tableKeys.every((key) => !key)) {
      if (tableStates.size > 0) {
        updateTableStates(tableStates, { type: "clear" });
      }
      return;
    }
    if (
      isEqual(tableKeys, previousTableKeys) ||
      (!isEmpty(tableStates) && !previousTableKeys)
    )
      return;

    for (const key of tableKeys) {
      if (!key || tableStates.has(key)) continue;
      updateTableStates(tableStates, {
        type: "create",
        key,
        tableState: {},
      });
    }

    if (previousTableKeys) {
      for (const key of previousTableKeys) {
        if (!key) continue;

        if (!tableKeys.includes(key)) {
          updateTableStates(tableStates, {
            type: "delete",
            key,
          });
        }
      }
    }
  }, [tableKeys]);

  return [tableStates, updateTableStates] as const;
};

/** If the backend returns the entire collection, we can slice and sort ourselves */
export const useClientSideSortingAndPagination = <TData,>(
  data: TData[] = [],
  tableState: TableStateType
) => {
  const { data: sortedData } = useSort(data, {
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  });
  const { data: paginatedData } = usePagination(sortedData, {
    page: tableState.page,
    pageSize: tableState.pageSize,
  });

  return {
    items: paginatedData,
    pagination: {
      page: tableState.page,
      pageSize: tableState.pageSize,
      total: data.length,
    },
    sorting: {
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
    },
  };
};

/** the subset of cursor-pagination state that's meaningfully persisted per-resource across reloads */
export type CursorPaginationPreferencesType = Pick<PaginationType, "pageSize"> &
  SortingType;

export type CursorPaginationTableStateType = CursorPaginationPreferencesType &
  Pick<PaginationType, "page">;

/**
 * Keep track of sorting and pagination state for an endpoint that only supports cursor-based
 * (continuation-token) pagination, where a page can only be reached by chaining the token returned
 * by the page before it - there is no way to request an arbitrary page directly.
 *
 * `page` and the token cache are kept in plain, in-memory state rather than alongside the
 * sticky-stored `pageSize`/sort preferences, since a stale page # persisted into another
 * resource's saved preferences (e.g. right as `storageKey` changes) would be invalid there.
 * @param initialState any combination of sorting / pagination props to use by default during first load
 * @param filters any values whose change should reset to page 1 and discard cached tokens - include
 * both the query filters (status, search, etc.) **and** the resource identity (e.g. `transactionId`)
 * so navigating to a different resource while the component is reused discards stale tokens too
 * @param storageKey sticky-storage key for the page size / sort preferences, ideally scoped per-resource
 * @returns `tableState` to pass to the `DataTable`'s sorting/pagination props, `updateSorting`/`updatePagination`
 * to pass to `onSortChange`/`onPaginationChange`, `registerNextPageToken` to call once a page's response
 * arrives, and the `continuationToken` to request for the page currently in `tableState.page`
 */
export const useCursorSortingAndPagination = (
  initialState: Partial<CursorPaginationTableStateType> = {},
  filters?: object,
  storageKey?: string
) => {
  const [preferences, setPreferences] =
    useStickyState<CursorPaginationPreferencesType>(
      {
        pageSize: ITEMS_PER_PAGE,
        sortBy: "index",
        sortDirection: SortDirection.ASCENDING,
        ...initialState,
      },
      storageKey ?? "cursor-table-state"
    );

  const [page, setPage] = useState(initialState.page ?? 1);
  const continuationTokensByPage = useRef<Map<number, string>>(new Map());
  const previousFilters = useRef(filters);
  const [maxCachedPage, setMaxCachedPage] = useState(1);

  const resetToFirstPage = () => {
    continuationTokensByPage.current = new Map();
    setMaxCachedPage(1);
    setPage(1);
  };

  if (!isEqual(previousFilters.current, filters)) {
    previousFilters.current = filters;
    resetToFirstPage();
  }

  const updateSorting = (sorting: SortingType) => {
    setPreferences((prev) => ({ ...prev, ...sorting }));
  };

  const updatePagination = (pagination: PaginationType) => {
    if (pagination.pageSize !== preferences.pageSize) {
      setPreferences((prev) => ({ ...prev, pageSize: pagination.pageSize }));
      resetToFirstPage();
      return;
    }

    if (
      pagination.page === 1 ||
      continuationTokensByPage.current.has(pagination.page)
    ) {
      setPage(pagination.page);
    }
  };

  const registerNextPageToken = useCallback(
    (forPage: number, token?: string | null) => {
      if (!token) return;

      continuationTokensByPage.current.set(forPage + 1, token);
      setMaxCachedPage((prev) => Math.max(prev, forPage + 1));
    },
    []
  );

  return {
    tableState: { page, ...preferences },
    updateSorting,
    updatePagination,
    registerNextPageToken,
    continuationToken:
      page === 1 ? undefined : continuationTokensByPage.current.get(page),
    availablePages: Array.from({ length: maxCachedPage }, (_, i) => i + 1),
  };
};
